import React, { useState, useEffect } from 'react'
import { Code, BookOpen, Zap, ArrowRight, List, Maximize2, Copy, Check, RefreshCw, RotateCcw, Loader2, ImagePlus, FileText, Image as ImageIcon } from 'lucide-react'
import { extractLensConstructs, parseSurfaceDiff, rebuildHTMLFromConstructs, type LensConstruct } from '../utils/lensParser'
import SlideshowOverlay from './SlideshowOverlay'
import { formatInsightToChatPrompt } from '../prompts'
import { useCopyToClipboard } from '../hooks/useCopyToClipboard'
import { AIService } from '../services/aiService'
import { generateInsightImage } from '../services/imageGenerationService'

// Format explanation text with styled inline code
const formatExplanationText = (text: string): string => {
  return text.replace(/`([^`]+)`/g, (_, code) => {
    return `<code style="font-family: ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, Monaco, Consolas, monospace; font-size: 14px; background-color: rgba(0, 0, 0, 0.06); padding: 2px 6px; border-radius: 4px; font-weight: 500; letter-spacing: -0.01em;">${code}</code>`
  })
}

interface NotebookViewProps {
  html: string
  sourceLanguage: string
  targetLanguage: string
  code?: string  // Full source code for regeneration context
  provider?: any  // Provider preference (fast/quality model)
  disableSlideshow?: boolean
  onHTMLUpdate?: (newHTML: string) => void  // Callback to sync regenerated insights back to parent
}

export const NotebookView: React.FC<NotebookViewProps> = ({
  html,
  sourceLanguage,
  targetLanguage,
  code = '',
  provider,
  disableSlideshow = false,
  onHTMLUpdate
}) => {
  // Extract constructs from the lens HTML
  const [constructs, setConstructs] = useState<LensConstruct[]>(() => extractLensConstructs(html))
  const [aiService] = useState(() => new AIService())

  // Update constructs when HTML changes
  useEffect(() => {
    setConstructs(extractLensConstructs(html))
  }, [html])

  // Slideshow state
  const [slideshow, setSlideshow] = useState({
    isActive: false,
    currentIndex: 0
  })

  // Regeneration state
  const [insightHistory, setInsightHistory] = useState<Map<number, LensConstruct>>(new Map())
  const [regeneratingInsights, setRegeneratingInsights] = useState<Set<number>>(new Set())

  // Image generation state
  const [imageCache, setImageCache] = useState<Map<number, string>>(new Map()) // index -> data URL
  const [generatingImages, setGeneratingImages] = useState<Set<number>>(new Set())
  const [imageViewMode, setImageViewMode] = useState<Map<number, 'text' | 'image'>>(new Map()) // index -> view mode

  // Slideshow handlers
  const enterSlideshow = (index: number) => {
    setSlideshow({ isActive: true, currentIndex: index })
    document.body.style.overflow = 'hidden'
  }

  const exitSlideshow = () => {
    setSlideshow({ isActive: false, currentIndex: 0 })
    document.body.style.overflow = 'auto'
  }

  const nextSlide = () => {
    if (slideshow.currentIndex < constructs.length - 1) {
      setSlideshow(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }))
    }
  }

  const prevSlide = () => {
    if (slideshow.currentIndex > 0) {
      setSlideshow(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 }))
    }
  }

  const goToSlide = (index: number) => {
    if (index >= 0 && index < constructs.length) {
      setSlideshow(prev => ({ ...prev, currentIndex: index }))
    }
  }

  // Regenerate handlers
  const handleRegenerate = async (construct: LensConstruct, index: number) => {
    if (!code) {
      console.error('No source code available for regeneration context')
      return
    }

    // Mark as regenerating
    setRegeneratingInsights(prev => new Set(prev).add(index))

    try {
      const originalInsight = {
        surface: construct.surface,
        concept: construct.concept,
        paradigm: construct.paradigm,
        code: construct.code
      }

      const result = await aiService.regenerateInsight(
        originalInsight,
        code,
        index,
        sourceLanguage,
        targetLanguage,
        provider  // Pass the selected provider (fast/quality model)
      )

      // Store original in history (one-time only, don't overwrite)
      if (!insightHistory.has(index)) {
        setInsightHistory(prev => new Map(prev).set(index, construct))
      }

      // Update construct with new insight
      setConstructs(prev => {
        const updated = [...prev]
        updated[index] = {
          ...updated[index],
          surface: result.surface,
          concept: result.concept,
          paradigm: result.paradigm
        }

        // Rebuild HTML with updated constructs and sync to parent
        if (onHTMLUpdate && html) {
          const newHTML = rebuildHTMLFromConstructs(html, updated)
          onHTMLUpdate(newHTML)
          console.log(`🔄 Synced regenerated insight ${index + 1} to parent`)
        }

        return updated
      })

      console.log(`✅ Regenerated insight ${index + 1}`)
    } catch (error) {
      console.error('Failed to regenerate insight:', error)
      // TODO: Show error toast
    } finally {
      setRegeneratingInsights(prev => {
        const updated = new Set(prev)
        updated.delete(index)
        return updated
      })
    }
  }

  const handleRevert = (index: number) => {
    const original = insightHistory.get(index)
    if (original) {
      setConstructs(prev => {
        const updated = [...prev]
        updated[index] = original

        // Rebuild HTML with reverted construct and sync to parent
        if (onHTMLUpdate && html) {
          const newHTML = rebuildHTMLFromConstructs(html, updated)
          onHTMLUpdate(newHTML)
          console.log(`🔄 Synced reverted insight ${index + 1} to parent`)
        }

        return updated
      })
      setInsightHistory(prev => {
        const updated = new Map(prev)
        updated.delete(index)
        return updated
      })
      console.log(`↩️ Reverted insight ${index + 1} to original`)
    }
  }

  // Image generation handler
  const handleGenerateImage = async (construct: LensConstruct, index: number) => {
    // Check if already cached
    if (imageCache.has(index)) {
      console.log(`📸 Image already cached for insight ${index + 1}`)
      // Switch to image view
      setImageViewMode(prev => new Map(prev).set(index, 'image'))
      return
    }

    // Mark as generating
    setGeneratingImages(prev => new Set(prev).add(index))

    try {
      console.log(`🎨 Generating image for insight ${index + 1}: ${construct.conceptName}`)

      const result = await generateInsightImage(construct, sourceLanguage, targetLanguage)

      if (result.success && result.imageUrl) {
        // Cache the image
        setImageCache(prev => new Map(prev).set(index, result.imageUrl!))
        // Switch to image view
        setImageViewMode(prev => new Map(prev).set(index, 'image'))
        console.log(`✅ Image generated and cached for insight ${index + 1}`)
      } else {
        console.error(`❌ Image generation failed for insight ${index + 1}:`, result.error)
        // TODO: Show error toast
      }
    } catch (error) {
      console.error('Failed to generate image:', error)
      // TODO: Show error toast
    } finally {
      setGeneratingImages(prev => {
        const updated = new Set(prev)
        updated.delete(index)
        return updated
      })
    }
  }

  // Toggle view mode between text and image
  const handleToggleViewMode = (index: number, mode: 'text' | 'image') => {
    setImageViewMode(prev => new Map(prev).set(index, mode))
  }

  // Keyboard navigation
  useEffect(() => {
    if (!slideshow.isActive) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault()
          nextSlide()
          break
        case 'ArrowLeft':
          e.preventDefault()
          prevSlide()
          break
        case 'Escape':
          e.preventDefault()
          exitSlideshow()
          break
        case 'Home':
          e.preventDefault()
          goToSlide(0)
          break
        case 'End':
          e.preventDefault()
          goToSlide(constructs.length - 1)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [slideshow.isActive, slideshow.currentIndex, constructs.length])

  if (constructs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md px-6">
          <div className="mb-4">
            <BookOpen className="w-16 h-16 mx-auto text-slate-300" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No Insights Available</h3>
          <p className="text-slate-600 mb-6">
            Generate insights first using the <span className="font-medium">Interactive Lens</span> tab, then come back here to view them in notebook format.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Page Header */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center gap-2 sm:gap-3 mb-3">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">
              Notebook
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm sm:text-base text-slate-600">
            <span className="hidden sm:inline">From</span>
            <span className="font-medium text-blue-600">{sourceLanguage}</span>
            <ArrowRight className="w-4 h-4" />
            <span className="font-medium text-orange-600">{targetLanguage}</span>
          </div>

          <div className="flex items-center gap-2 mt-3 text-xs sm:text-sm text-slate-500">
            <List className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{constructs.length} construct{constructs.length !== 1 ? 's' : ''} identified</span>
          </div>
        </div>

        {/* Notebook Cells */}
        {constructs.map((construct, index) => (
          <NotebookCell
            key={construct.id}
            construct={construct}
            index={index}
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            onEnterSlideshow={enterSlideshow}
            onRegenerate={handleRegenerate}
            onRevert={handleRevert}
            hasHistory={insightHistory.has(index)}
            isRegenerating={regeneratingInsights.has(index)}
            disableSlideshow={disableSlideshow}
            onGenerateImage={handleGenerateImage}
            onToggleViewMode={handleToggleViewMode}
            imageUrl={imageCache.get(index)}
            isGeneratingImage={generatingImages.has(index)}
            viewMode={imageViewMode.get(index) || 'text'}
          />
        ))}

        {/* Footer */}
        <div className="mt-8 sm:mt-12 p-4 sm:p-6 bg-white rounded-xl border border-slate-200 text-center shadow-sm">
          <p className="text-xs sm:text-sm text-slate-500">
            💡 <span className="font-medium">Tip:</span> Each construct shows syntax transformation, semantic meaning, and paradigm differences
          </p>
        </div>
      </main>

      {/* Slideshow Overlay */}
      {slideshow.isActive && (
        <SlideshowOverlay
          constructs={constructs}
          currentIndex={slideshow.currentIndex}
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
          onNext={nextSlide}
          onPrev={prevSlide}
          onExit={exitSlideshow}
        />
      )}
    </div>
  )
}

interface NotebookCellProps {
  construct: LensConstruct
  index: number
  sourceLanguage: string
  targetLanguage: string
  onEnterSlideshow: (index: number) => void
  onRegenerate: (construct: LensConstruct, index: number) => void
  onRevert: (index: number) => void
  hasHistory: boolean
  isRegenerating: boolean
  disableSlideshow?: boolean
  onGenerateImage: (construct: LensConstruct, index: number) => void
  onToggleViewMode: (index: number, mode: 'text' | 'image') => void
  imageUrl?: string
  isGeneratingImage: boolean
  viewMode: 'text' | 'image'
}

const NotebookCell: React.FC<NotebookCellProps> = ({
  construct,
  index,
  sourceLanguage,
  targetLanguage,
  onEnterSlideshow,
  onRegenerate,
  onRevert,
  hasHistory,
  isRegenerating,
  disableSlideshow = false,
  onGenerateImage,
  onToggleViewMode,
  imageUrl,
  isGeneratingImage,
  viewMode
}) => {
  const { source, target } = parseSurfaceDiff(construct.surface)
  const { copied, copy: copyToClipboard } = useCopyToClipboard()

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    try {
      const codeSnippet = target || construct.code

      const content = formatInsightToChatPrompt({
        conceptName: construct.conceptName,
        codeElement: codeSnippet,
        sourceLanguage,
        targetLanguage,
        source: source || '(similar)',
        target: codeSnippet,
        concept: construct.concept,
        paradigm: construct.paradigm,
      })

      await copyToClipboard(content)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-md hover:shadow-lg transition-shadow duration-200 mb-6 sm:mb-8">
      {/* Section Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500 text-white font-semibold text-xs sm:text-sm">
          {index + 1}
        </div>
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex-1">
          {construct.conceptName}
        </h2>
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Copy Button - Touch-friendly on mobile */}
          <div className="relative group/copy">
            <button
              onClick={handleCopy}
              className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-slate-200 active:bg-slate-300 rounded-lg transition-colors flex items-center justify-center"
              style={{ minWidth: '40px', minHeight: '40px' }}
              aria-label="Copy insight"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-slate-400 group-hover/copy:text-slate-600" />
              )}
            </button>
            {/* Bottom tooltip */}
            {!copied && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                Copy
              </div>
            )}
          </div>

          {/* Revert Button (show if hasHistory) */}
          {hasHistory && (
            <div className="relative group/revert">
              <button
                onClick={() => onRevert(index)}
                data-testid="notebook-button-revert"
                aria-label="Revert to original insight"
                className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-blue-100 active:bg-blue-200 rounded-lg transition-colors flex items-center justify-center"
                style={{ minWidth: '40px', minHeight: '40px' }}
              >
                <RotateCcw className="w-4 h-4 text-blue-600" />
              </button>
              {/* Bottom tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/revert:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                Revert
              </div>
            </div>
          )}

          {/* Regenerate Button */}
          <div className="relative group/regenerate">
            <button
              onClick={() => onRegenerate(construct, index)}
              disabled={isRegenerating}
              data-testid="notebook-button-regenerate"
              aria-label="Regenerate this insight with AI"
              className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-orange-100 active:bg-orange-200 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minWidth: '40px', minHeight: '40px' }}
            >
              {isRegenerating ? (
                <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 text-orange-500" />
              )}
            </button>
            {/* Bottom tooltip */}
            {!isRegenerating && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/regenerate:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                Regenerate
              </div>
            )}
          </div>

          {/* Image Generation Button */}
          <div className="relative group/image">
            <button
              onClick={() => onGenerateImage(construct, index)}
              disabled={isGeneratingImage}
              data-testid="notebook-button-generate-image"
              aria-label="Generate visual diagram for this insight"
              className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-purple-100 active:bg-purple-200 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minWidth: '40px', minHeight: '40px' }}
            >
              {isGeneratingImage ? (
                <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
              ) : (
                <ImagePlus className="w-4 h-4 text-purple-500" />
              )}
            </button>
            {/* Bottom tooltip */}
            {!isGeneratingImage && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/image:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                {imageUrl ? 'View Image' : 'Generate Image'}
              </div>
            )}
          </div>

          {/* Slideshow Button - Touch-friendly on mobile */}
          {!disableSlideshow && (
            <button
              onClick={() => onEnterSlideshow(index)}
              className="w-10 h-10 sm:w-8 sm:h-8 p-2 hover:bg-slate-200 active:bg-slate-300 rounded-lg transition-colors group"
              style={{ minWidth: '40px', minHeight: '40px' }}
              aria-label="Enter slideshow mode"
              title="Present this construct"
            >
              <Maximize2 className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
            </button>
          )}
        </div>
      </div>

      {/* Section Content */}
      <div className="space-y-4 sm:space-y-6">
        {/* Code Comparison */}
        {(source || target || construct.code) && (
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 min-w-0">
              {/* Source Language (Left - Known) */}
              <div className="space-y-2 min-w-0">
                <span className="inline-flex items-center bg-slate-100 text-slate-500 px-2 sm:px-3 py-1 rounded-md text-xs font-medium uppercase tracking-wider">
                  {sourceLanguage}
                </span>
                <pre className="bg-slate-900 rounded-lg p-3 sm:p-6 text-sm sm:text-[15px] leading-relaxed text-slate-100 font-mono overflow-x-auto border border-slate-700 max-w-full">
                  <code className="block whitespace-pre">
                    {source || '(similar)'}
                  </code>
                </pre>
              </div>

              {/* Target Language (Right - Learning) */}
              <div className="space-y-2 min-w-0">
                <span className="inline-flex items-center bg-slate-100 text-slate-500 px-2 sm:px-3 py-1 rounded-md text-xs font-medium uppercase tracking-wider">
                  {targetLanguage}
                </span>
                <pre className="bg-slate-900 rounded-lg p-3 sm:p-6 text-sm sm:text-[15px] leading-relaxed text-slate-100 font-mono overflow-x-auto border border-slate-700 max-w-full">
                  <code className="block whitespace-pre">
                    {target || construct.code}
                  </code>
                </pre>
              </div>
          </div>
        )}

        {/* View Toggle - Show when image is available */}
        {imageUrl && (
          <div className="flex items-center gap-2 mt-6 sm:mt-8 mb-4">
            <span className="text-xs sm:text-sm text-slate-600 font-medium">View:</span>
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => onToggleViewMode(index, 'text')}
                data-testid="notebook-toggle-text-view"
                aria-label="Show text explanations"
                className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  viewMode === 'text'
                    ? 'bg-white text-slate-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Text</span>
              </button>
              <button
                onClick={() => onToggleViewMode(index, 'image')}
                data-testid="notebook-toggle-image-view"
                aria-label="Show visual diagram"
                className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  viewMode === 'image'
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ImageIcon className="w-3.5 h-3.5" />
                <span>Image</span>
              </button>
            </div>
          </div>
        )}

        {/* Image View - Show diagram when in image mode */}
        {viewMode === 'image' && imageUrl && (
          <div className="mt-6 sm:mt-8">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                <h3 className="text-sm font-semibold text-purple-900">Visual Diagram</h3>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <img
                  src={imageUrl}
                  alt={`Visual diagram for ${construct.conceptName}`}
                  className="w-full h-auto rounded-lg"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
              <p className="text-xs text-purple-700 mt-3 text-center">
                AI-generated diagram explaining {construct.conceptName} ({sourceLanguage} → {targetLanguage})
              </p>
            </div>
          </div>
        )}

        {/* Explanations - All Levels (only show in text view) */}
        {viewMode === 'text' && (
        <div className="space-y-3 sm:space-y-4 mt-6 sm:mt-8">
          {/* L2: Semantic Meaning */}
          {construct.concept && (
            <div className="flex items-start gap-2 sm:gap-3 border-l-[3px] border-emerald-500 bg-emerald-50/20 pl-3 sm:pl-4 pr-3 sm:pr-4 py-3 sm:py-4 rounded-r-lg hover:bg-emerald-50/30 transition-colors duration-200">
              <div className="flex-shrink-0">
                <BookOpen className="w-5 h-5 text-emerald-600" strokeWidth={2} />
              </div>
              <div className="flex-1 max-w-[70ch]">
                <h3 className="text-emerald-700 font-semibold text-sm uppercase tracking-wider mb-2">
                  Semantic Meaning
                </h3>
                <div
                  className="text-[#1A1A1A]"
                  style={{
                    fontFamily: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '1.6',
                    letterSpacing: '-0.005em'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatExplanationText(construct.concept)
                  }}
                />
              </div>
            </div>
          )}

          {/* L3: Paradigm Shift */}
          {construct.paradigm && (
            <div className="flex items-start gap-2 sm:gap-3 border-l-[3px] border-purple-500 bg-purple-50/20 pl-3 sm:pl-4 pr-3 sm:pr-4 py-3 sm:py-4 rounded-r-lg hover:bg-purple-50/30 transition-colors duration-200">
              <div className="flex-shrink-0">
                <Zap className="w-5 h-5 text-purple-600" strokeWidth={2} />
              </div>
              <div className="flex-1 max-w-[70ch]">
                <h3 className="text-purple-700 font-semibold text-sm uppercase tracking-wider mb-2">
                  Paradigm Shift
                </h3>
                <div
                  className="text-[#1A1A1A]"
                  style={{
                    fontFamily: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '1.6',
                    letterSpacing: '-0.005em'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatExplanationText(construct.paradigm)
                  }}
                />
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </section>
  )
}

export default NotebookView
